import { isNil, trim } from 'lodash/fp';
import { useMemo } from 'react';
import { type ResidualValueSettingsPayload } from '../../../../../../api/types';
import { type NumberFormatFn } from '../../../../../../utilities/useFormats';
import { mileageFormatAmount } from '../../../../AddVehiclePage/AddVariantPage/helper';
import { getFinderVehicleFilter, getVariantFilter } from '../../../shared/useVehicleSuiteIdsFromParameters';
import hasVehicleParameterFilter from '../../helpers/hasVehicleParameterFilter';
import { type FinanceProductFormValues, type FinderVehicleReferenceParameterPayload } from '../../index';
import { type LeasePurchaseTableRecord } from './shared';

type UseVehicleReferenceParameterRecordsReturn = {
    variant: LeasePurchaseTableRecord[];
    finderVehicle: LeasePurchaseTableRecord[];
};

export const variantParameterKey = 'vehicleParameter';
export const finderVehicleParameterKey = 'finderVehicleParameter';

export const buildFinderVehicleReferenceParameterName = (
    parameters: FinderVehicleReferenceParameterPayload,
    mileageUnit: string
) => {
    const name: string[] = [];

    if (parameters.engineType.length) {
        name.push(parameters.engineType.join(', '));
    }

    if (trim(parameters.variants).length) {
        name.push(trim(parameters.variants));
    }

    if (!isNil(parameters.mileage?.from) || !isNil(parameters.mileage?.to)) {
        const mileage = [
            !isNil(parameters.mileage?.from) ? `${mileageFormatAmount(parameters.mileage?.from)}${mileageUnit}` : null,
            !isNil(parameters.mileage?.to) ? `${mileageFormatAmount(parameters.mileage?.to)}${mileageUnit}` : null,
        ];

        name.push(mileage.filter(Boolean).join('-'));
    }

    if (!isNil(parameters.modelYear?.from) || !isNil(parameters.modelYear?.to)) {
        const modelYear = [
            !isNil(parameters.modelYear?.from) ? parameters.modelYear.from : null,
            !isNil(parameters.modelYear?.to) ? parameters.modelYear.to : null,
        ];

        name.push(modelYear.filter(Boolean).join('-'));
    }

    if (!name.length) {
        return null;
    }

    return name.join(', ');
};

const useVehicleReferenceParameterRecords = (
    vehicleReferenceParameters: FinanceProductFormValues['vehicleReferenceParameters'],
    residualValueSetting: ResidualValueSettingsPayload,
    terms: number[],
    averageMileages: number[],
    formatAmount: NumberFormatFn,
    mileageUnitLabel: string
): UseVehicleReferenceParameterRecordsReturn =>
    useMemo<UseVehicleReferenceParameterRecordsReturn>(() => {
        const vehicleReferenceParameterData = {
            finderVehicle: [],
            variant: [],
        };

        const finderVehicleFilter = getFinderVehicleFilter(vehicleReferenceParameters);
        const variantFilter = getVariantFilter(vehicleReferenceParameters);

        if (
            !residualValueSetting ||
            !terms?.length ||
            (!hasVehicleParameterFilter(finderVehicleFilter) && !hasVehicleParameterFilter(variantFilter))
        ) {
            return vehicleReferenceParameterData;
        }

        const variantReferenceParameterId = vehicleReferenceParameters.variant.engineType.length
            ? vehicleReferenceParameters.variant.engineType.join(', ')
            : null;
        const finderVehicleReferenceParameterId = buildFinderVehicleReferenceParameterName(
            vehicleReferenceParameters.finderVehicle,
            mileageUnitLabel
        );

        const { table } = residualValueSetting;

        const getValue = (variantSuiteId: string, term: number, mileage: number) => {
            const row = table.find(
                row => row.variantSuiteId === variantSuiteId && row.term === term && row.mileage === mileage
            );

            return row?.value;
        };

        if (hasVehicleParameterFilter(variantFilter)) {
            const variantMileage: LeasePurchaseTableRecord[] = averageMileages.map(mileage => {
                const termsValue = {};

                terms.forEach(term => {
                    termsValue[`term${term}`] = getValue(variantParameterKey, term, mileage);
                });

                return {
                    key: variantParameterKey,
                    variantSuiteId: variantParameterKey,
                    variantId: variantParameterKey,
                    mileage,
                    variantName: variantReferenceParameterId,
                    variantNameMileage: `${variantReferenceParameterId} | ${formatAmount(mileage)} ${mileageUnitLabel}`,
                    ...termsValue,
                };
            });
            vehicleReferenceParameterData.variant = variantMileage;
        }

        if (hasVehicleParameterFilter(finderVehicleFilter)) {
            const finderVehicleMileage: LeasePurchaseTableRecord[] = averageMileages.map(mileage => {
                const termsValue = {};

                terms.forEach(term => {
                    termsValue[`term${term}`] = getValue(finderVehicleParameterKey, term, mileage);
                });

                return {
                    key: finderVehicleParameterKey,
                    variantSuiteId: finderVehicleParameterKey,
                    variantId: finderVehicleParameterKey,
                    mileage,
                    variantName: finderVehicleReferenceParameterId,
                    variantNameMileage: `${finderVehicleReferenceParameterId} | ${formatAmount(
                        mileage
                    )} ${mileageUnitLabel}`,
                    ...termsValue,
                };
            });
            vehicleReferenceParameterData.finderVehicle = finderVehicleMileage;
        }

        return vehicleReferenceParameterData;
    }, [averageMileages, formatAmount, mileageUnitLabel, residualValueSetting, terms, vehicleReferenceParameters]);

export default useVehicleReferenceParameterRecords;
